import { useMemo } from 'react';
import { Wifi } from 'lucide-react';

/**
 * WiFi Speed classification based on FCC and industry standards
 */
const SPEED_TIERS = {
  poor: { min: 0, max: 5, label: 'Poor', color: '#EF4444', bgColor: '#FEE2E2' },
  basic: { min: 5, max: 25, label: 'Basic', color: '#F97316', bgColor: '#FFEDD5' },
  good: { min: 25, max: 50, label: 'Good', color: '#EAB308', bgColor: '#FEF9C3' },
  fast: { min: 50, max: 100, label: 'Fast', color: '#22C55E', bgColor: '#DCFCE7' },
  high_speed: { min: 100, max: 300, label: 'High-Speed', color: '#14B8A6', bgColor: '#CCFBF1' },
  ultra_high: { min: 300, max: 1000, label: 'Ultra High', color: '#8B5CF6', bgColor: '#EDE9FE' },
};

function getSpeedTier(speedMbps) {
  if (!speedMbps || speedMbps <= 0) return null;
  if (speedMbps < 5) return 'poor';
  if (speedMbps < 25) return 'basic';
  if (speedMbps < 50) return 'good';
  if (speedMbps < 100) return 'fast';
  if (speedMbps < 300) return 'high_speed';
  return 'ultra_high';
}

/**
 * Semi-circular WiFi Speed Gauge Component
 * Displays download speed with color-coded dial
 */
export default function WiFiSpeedGauge({ 
  speedMbps, 
  uploadMbps,
  latencyMs,
  size = 'md',  // sm, md, lg
  showLabel = true,
  showDetails = false,
  compact = false,
  className = ''
}) {
  const tier = useMemo(() => getSpeedTier(speedMbps), [speedMbps]);
  const tierData = tier ? SPEED_TIERS[tier] : null;
  
  // Size configurations
  const sizes = {
    sm: { width: 60, height: 35, fontSize: 10, labelSize: 8, strokeWidth: 6 },
    md: { width: 100, height: 55, fontSize: 14, labelSize: 10, strokeWidth: 8 },
    lg: { width: 140, height: 75, fontSize: 18, labelSize: 12, strokeWidth: 10 },
  };
  
  const config = sizes[size] || sizes.md;
  
  // Calculate arc parameters for semi-circle
  const centerX = config.width / 2;
  const centerY = config.height - 5;
  const radius = config.width / 2 - config.strokeWidth;
  
  // Calculate fill percentage (0-180 degrees)
  // Map speed to 0-100% where 300+ Mbps = 100%
  const maxSpeed = 300;
  const percentage = Math.min((speedMbps || 0) / maxSpeed * 100, 100);
  const angle = (percentage / 100) * 180;
  
  // Calculate arc path
  const startAngle = 180; // Left side
  const endAngle = 180 - angle; // Sweep towards right
  
  const startX = centerX + radius * Math.cos((startAngle * Math.PI) / 180);
  const startY = centerY - radius * Math.sin((startAngle * Math.PI) / 180);
  const endX = centerX + radius * Math.cos((endAngle * Math.PI) / 180);
  const endY = centerY - radius * Math.sin((endAngle * Math.PI) / 180);
  
  const largeArcFlag = angle > 90 ? 1 : 0;
  
  const backgroundPath = `M ${centerX - radius} ${centerY} A ${radius} ${radius} 0 0 1 ${centerX + radius} ${centerY}`;
  const fillPath = angle > 0 
    ? `M ${startX} ${startY} A ${radius} ${radius} 0 ${largeArcFlag} 1 ${endX} ${endY}`
    : '';

  if (!speedMbps && speedMbps !== 0) {
    // No speed data - show placeholder
    return (
      <div className={`flex items-center gap-1.5 text-gray-400 ${className}`}>
        <Wifi className="w-4 h-4" />
        <span className="text-xs">No speed data</span>
      </div>
    );
  }

  if (compact) {
    // Compact inline badge version
    return (
      <div 
        className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-full text-xs font-medium ${className}`}
        style={{ 
          backgroundColor: tierData?.bgColor || '#F3F4F6',
          color: tierData?.color || '#6B7280'
        }}
        title={`Download: ${speedMbps?.toFixed(1)} Mbps${uploadMbps ? ` | Upload: ${uploadMbps?.toFixed(1)} Mbps` : ''}${latencyMs ? ` | Latency: ${latencyMs?.toFixed(0)}ms` : ''}`}
      >
        <Wifi className="w-3 h-3" />
        <span>{speedMbps?.toFixed(0)} Mbps</span>
        {showLabel && tierData && (
          <span className="opacity-75">• {tierData.label}</span>
        )}
      </div>
    );
  }

  return (
    <div className={`flex flex-col items-center ${className}`}>
      {/* SVG Gauge */}
      <svg width={config.width} height={config.height} className="overflow-visible">
        {/* Background arc (gray) */}
        <path
          d={backgroundPath}
          fill="none"
          stroke="#E5E7EB"
          strokeWidth={config.strokeWidth}
          strokeLinecap="round"
        />
        
        {/* Colored fill arc */}
        {fillPath && (
          <path
            d={fillPath}
            fill="none"
            stroke={tierData?.color || '#9CA3AF'}
            strokeWidth={config.strokeWidth}
            strokeLinecap="round"
            style={{
              transition: 'stroke-dashoffset 0.5s ease-in-out'
            }}
          />
        )}
        
        {/* Speed value text */}
        <text
          x={centerX}
          y={centerY - 8}
          textAnchor="middle"
          fill={tierData?.color || '#374151'}
          fontSize={config.fontSize}
          fontWeight="bold"
        >
          {speedMbps?.toFixed(0)}
        </text>
        
        {/* Mbps label */}
        <text
          x={centerX}
          y={centerY + 2}
          textAnchor="middle"
          fill="#6B7280"
          fontSize={config.labelSize}
        >
          Mbps
        </text>
      </svg>
      
      {/* Tier label below gauge */}
      {showLabel && tierData && (
        <div 
          className="mt-1 px-2 py-0.5 rounded-full text-xs font-medium"
          style={{ 
            backgroundColor: tierData.bgColor,
            color: tierData.color
          }}
        >
          {tierData.label}
        </div>
      )}
      
      {/* Additional details */}
      {showDetails && (
        <div className="mt-2 flex gap-3 text-xs text-gray-500">
          {uploadMbps !== undefined && (
            <span>↑ {uploadMbps?.toFixed(1)} Mbps</span>
          )}
          {latencyMs !== undefined && (
            <span>{latencyMs?.toFixed(0)}ms ping</span>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * Mini inline speed indicator for cards
 */
export function WiFiSpeedBadge({ speedMbps, className = '' }) {
  const tier = getSpeedTier(speedMbps);
  const tierData = tier ? SPEED_TIERS[tier] : null;
  
  if (!speedMbps) return null;
  
  return (
    <span 
      className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-xs font-medium ${className}`}
      style={{ 
        backgroundColor: tierData?.bgColor || '#F3F4F6',
        color: tierData?.color || '#6B7280'
      }}
    >
      <Wifi className="w-3 h-3" />
      {speedMbps?.toFixed(0)}
    </span>
  );
}

export { SPEED_TIERS, getSpeedTier };
